import React from 'react';
import { 
  Phone, MessageCircle, Mail, Globe, MapPin, 
  Linkedin, Twitter, Instagram, Download, 
  User, Building2 
} from 'lucide-react';
import { BusinessCard as BusinessCardType } from '../types';
import { cardServices } from '../services/cardServices';

interface BusinessCardProps {
  card: BusinessCardType;
  className?: string;
}

const BusinessCard: React.FC<BusinessCardProps> = ({ card, className = '' }) => {
  const actions = cardServices.generateContactActions(card);

  const handleAction = async (action: string, url?: string) => {
    switch (action) {
      case 'download':
        cardServices.downloadVCard(card);
        break;
      case 'share':
        await cardServices.shareCard(card);
        break;
      case 'qr':
        await cardServices.downloadQRCode(card);
        break;
      case 'copy':
        if (url) {
          await cardServices.copyToClipboard(url);
        }
        break;
      default:
        if (url) {
          window.open(url, '_blank');
        }
    }
  };

  const renderModernTheme = () => (
    <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-primary via-primary-light to-primary-dark p-8 text-white shadow-2xl">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 right-0 w-32 h-32 bg-white rounded-full -translate-y-16 translate-x-16"></div>
        <div className="absolute bottom-0 left-0 w-24 h-24 bg-white rounded-full translate-y-12 -translate-x-12"></div>
      </div>
      <div className="relative z-10">
        {/* Header */}
        <div className="text-center mb-8">
          {card.image && (
            <div className="mx-auto mb-6">
              <img
                src={card.image}
                alt={card.name}
                className="w-24 h-24 rounded-full object-cover border-4 border-white shadow-lg"
              />
            </div>
          )}
          
          <div className="space-y-2">
            <h1 className="text-3xl font-bold font-english">{card.name}</h1>
            <div className="flex items-center justify-center space-x-2 text-lg opacity-90">
              <Building2 className="h-5 w-5" />
              <span>{card.position}</span>
            </div>
            <p className="text-lg opacity-80">{card.company}</p>
          </div>
        </div>

        {/* Contact Actions */}
        <div className="grid grid-cols-3 gap-4 mb-8">
          <button
            onClick={() => handleAction('phone', actions.phone)}
            className="flex flex-col items-center space-y-2 p-4 bg-white bg-opacity-20 rounded-xl hover:bg-opacity-30 transition-all"
          >
            <Phone className="h-6 w-6" />
            <span className="text-sm font-medium">Call</span>
          </button>
          
          <button
            onClick={() => handleAction('whatsapp', actions.whatsapp)}
            className="flex flex-col items-center space-y-2 p-4 bg-white bg-opacity-20 rounded-xl hover:bg-opacity-30 transition-all"
          >
            <MessageCircle className="h-6 w-6" />
            <span className="text-sm font-medium">WhatsApp</span>
          </button>
          
          <button
            onClick={() => handleAction('email', actions.email)}
            className="flex flex-col items-center space-y-2 p-4 bg-white bg-opacity-20 rounded-xl hover:bg-opacity-30 transition-all"
          >
            <Mail className="h-6 w-6" />
            <span className="text-sm font-medium">Email</span>
          </button>
        </div>

        {/* Additional Info */}
        <div className="space-y-4">
          {card.address && (
            <div className="flex items-center space-x-3 p-3 bg-white bg-opacity-10 rounded-lg">
              <MapPin className="h-5 w-5 flex-shrink-0" />
              <span className="text-sm">{card.address}</span>
            </div>
          )}
          
          {card.website && (
            <div className="flex items-center space-x-3 p-3 bg-white bg-opacity-10 rounded-lg">
              <Globe className="h-5 w-5 flex-shrink-0" />
              <span className="text-sm">{card.website}</span>
            </div>
          )}
          
          {card.description && (
            <p className="text-sm opacity-90 text-center italic">{card.description}</p>
          )}
        </div>

        {/* Social Media */}
        {(card.linkedin || card.twitter || card.instagram) && (
          <div className="flex justify-center space-x-4 mt-6">
            {card.linkedin && (
              <button
                onClick={() => handleAction('linkedin', actions.linkedin!)}
                className="p-2 bg-white bg-opacity-20 rounded-full hover:bg-opacity-30 transition-all"
              >
                <Linkedin className="h-5 w-5" />
              </button>
            )}
            {card.twitter && (
              <button
                onClick={() => handleAction('twitter', actions.twitter!)}
                className="p-2 bg-white bg-opacity-20 rounded-full hover:bg-opacity-30 transition-all"
              >
                <Twitter className="h-5 w-5" />
              </button>
            )}
            {card.instagram && (
              <button
                onClick={() => handleAction('instagram', actions.instagram!)}
                className="p-2 bg-white bg-opacity-20 rounded-full hover:bg-opacity-30 transition-all"
              >
                <Instagram className="h-5 w-5" />
              </button>
            )}
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex justify-center space-x-3 mt-8">
          <button
            onClick={() => handleAction('download')}
            className="flex items-center space-x-2 px-4 py-2 bg-white text-primary rounded-lg hover:bg-gray-100 transition-colors"
          >
            <Download className="h-4 w-4" />
            <span className="text-sm font-medium">Save Contact</span>
          </button>
          
          {/* <button
            onClick={() => handleAction('share')}
            className="flex items-center space-x-2 px-4 py-2 bg-white text-primary rounded-lg hover:bg-gray-100 transition-colors"
          >
            <Share2 className="h-4 w-4" />
            <span className="text-sm font-medium">Share</span>
          </button> */}
{/*           
          <button
            onClick={() => handleAction('qr')}
            className="flex items-center space-x-2 px-4 py-2 bg-white text-primary rounded-lg hover:bg-gray-100 transition-colors"
          >
            <QrCode className="h-4 w-4" />
            <span className="text-sm font-medium">QR</span>
          </button> */}
        </div>
      </div>
    </div>
  );

  const renderClassicTheme = () => (
    <div className="bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden">
      <div className="md:flex">
        {/* Left Side - Image */}
        <div className="md:w-1/3 bg-gray-50 flex items-center justify-center p-8">
          {card.image ? (
            <img
              src={card.image}
              alt={card.name}
              className="w-32 h-32 rounded-lg object-cover shadow-md"
            />
          ) : (
            <div className="w-32 h-32 bg-primary rounded-lg flex items-center justify-center">
              <User className="h-16 w-16 text-white" />
            </div>
          )}
        </div>

        {/* Right Side - Content */}
        <div className="md:w-2/3 p-8">
          {/* Header */}
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-gray-900 font-english mb-2">{card.name}</h1>
            <div className="flex items-center space-x-2 text-gray-600 mb-1">
              <Building2 className="h-4 w-4" />
              <span className="font-medium">{card.position}</span>
            </div>
            <p className="text-gray-500">{card.company}</p>
          </div>

          {/* Contact Actions */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
            <button
              onClick={() => handleAction('phone', actions.phone)}
              className="flex items-center justify-center space-x-2 px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark transition-colors"
            >
              <Phone className="h-4 w-4" />
              <span className="text-sm font-medium">Call</span>
            </button>
            
            <button
              onClick={() => handleAction('whatsapp', actions.whatsapp)}
              className="flex items-center justify-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
            >
              <MessageCircle className="h-4 w-4" />
              <span className="text-sm font-medium">WhatsApp</span>
            </button>
            
            <button
              onClick={() => handleAction('email', actions.email)}
              className="flex items-center justify-center space-x-2 px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors"
            >
              <Mail className="h-4 w-4" />
              <span className="text-sm font-medium">Email</span>
            </button>
          </div>

          {/* Additional Info */}
          <div className="space-y-3 mb-6">
            {card.address && (
              <div className="flex items-center space-x-3">
                <MapPin className="h-4 w-4 text-gray-400 flex-shrink-0" />
                <span className="text-sm text-gray-600">{card.address}</span>
              </div>
            )}
            
            {card.website && (
              <div className="flex items-center space-x-3">
                <Globe className="h-4 w-4 text-gray-400 flex-shrink-0" />
                <span className="text-sm text-gray-600">{card.website}</span>
              </div>
            )}
            
            {card.description && (
              <p className="text-sm text-gray-600 italic">{card.description}</p>
            )}
          </div>

          {/* Social Media */}
          {(card.linkedin || card.twitter || card.instagram) && (
            <div className="flex space-x-3 mb-6">
              {card.linkedin && (
                <button
                  onClick={() => handleAction('linkedin', actions.linkedin!)}
                  className="p-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                >
                  <Linkedin className="h-4 w-4" />
                </button>
              )}
              {card.twitter && (
                <button
                  onClick={() => handleAction('twitter', actions.twitter!)}
                  className="p-2 bg-blue-400 text-white rounded-md hover:bg-blue-500 transition-colors"
                >
                  <Twitter className="h-4 w-4" />
                </button>
              )}
              {card.instagram && (
                <button
                  onClick={() => handleAction('instagram', actions.instagram!)}
                  className="p-2 bg-pink-600 text-white rounded-md hover:bg-pink-700 transition-colors"
                >
                  <Instagram className="h-4 w-4" />
                </button>
              )}
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => handleAction('download')}
              className="flex items-center space-x-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
            >
              <Download className="h-4 w-4" />
              <span className="text-sm font-medium">Save Contact</span>
            </button>
            
            {/* <button
              onClick={() => handleAction('share')}
              className="flex items-center space-x-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
            >
              <Share2 className="h-4 w-4" />
              <span className="text-sm font-medium">Share</span>
            </button>
            
            <button
              onClick={() => handleAction('qr')}
              className="flex items-center space-x-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
            >
              <QrCode className="h-4 w-4" />
              <span className="text-sm font-medium">QR Code</span>
            </button> */}
          </div>
        </div>
      </div>
    </div>
  );

  const renderMinimalTheme = () => (
    <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-8 max-w-md mx-auto">
      {/* Header */}
      <div className="text-center mb-8">
        {card.image && (
          <div className="mx-auto mb-6">
            <img
              src={card.image}
              alt={card.name}
              className="w-20 h-20 rounded-full object-cover border-2 border-gray-200"
            />
          </div>
        )}
        
        <h1 className="text-2xl font-light text-gray-900 font-english mb-2">{card.name}</h1>
        <p className="text-sm text-gray-500 mb-1">{card.position}</p>
        <p className="text-xs text-gray-400">{card.company}</p>
      </div>

      {/* Contact Actions */}
      <div className="space-y-3 mb-8">
        <button
          onClick={() => handleAction('phone', actions.phone)}
          className="w-full flex items-center justify-center space-x-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
        >
          <Phone className="h-4 w-4 text-gray-500" />
          <span className="text-sm text-gray-700">Call</span>
        </button>
        
        <button
          onClick={() => handleAction('whatsapp', actions.whatsapp)}
          className="w-full flex items-center justify-center space-x-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
        >
          <MessageCircle className="h-4 w-4 text-gray-500" />
          <span className="text-sm text-gray-700">WhatsApp</span>
        </button>
        
        <button
          onClick={() => handleAction('email', actions.email)}
          className="w-full flex items-center justify-center space-x-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
        >
          <Mail className="h-4 w-4 text-gray-500" />
          <span className="text-sm text-gray-700">Email</span>
        </button>
      </div>

      {/* Additional Info */}
      <div className="space-y-3 mb-8">
        {card.address && (
          <div className="flex items-center space-x-3 text-sm text-gray-600">
            <MapPin className="h-4 w-4 text-gray-400 flex-shrink-0" />
            <span>{card.address}</span>
          </div>
        )}
        
        {card.website && (
          <div className="flex items-center space-x-3 text-sm text-gray-600">
            <Globe className="h-4 w-4 text-gray-400 flex-shrink-0" />
            <span>{card.website}</span>
          </div>
        )}
        
        {card.description && (
          <p className="text-sm text-gray-500 text-center leading-relaxed">{card.description}</p>
        )}
      </div>

      {/* Social Media */}
      {(card.linkedin || card.twitter || card.instagram) && (
        <div className="flex justify-center space-x-4 mb-8">
          {card.linkedin && (
            <button
              onClick={() => handleAction('linkedin', actions.linkedin!)}
              className="p-2 text-gray-400 hover:text-blue-600 transition-colors"
            >
              <Linkedin className="h-5 w-5" />
            </button>
          )}
          {card.twitter && (
            <button
              onClick={() => handleAction('twitter', actions.twitter!)}
              className="p-2 text-gray-400 hover:text-blue-400 transition-colors"
            >
              <Twitter className="h-5 w-5" />
            </button>
          )}
          {card.instagram && (
            <button
              onClick={() => handleAction('instagram', actions.instagram!)}
              className="p-2 text-gray-400 hover:text-pink-600 transition-colors"
            >
              <Instagram className="h-5 w-5" />
            </button>
          )}
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex justify-center space-x-4">
        <button
          onClick={() => handleAction('download')}
          className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
          title="Save Contact"
        >
          <Download className="h-5 w-5" />
        </button>
        
        {/* <button
          onClick={() => handleAction('share')}
          className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
          title="Share"
        >
          <Share2 className="h-5 w-5" />
        </button>
        
        <button
          onClick={() => handleAction('qr')}
          className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
          title="QR Code"
        >
          <QrCode className="h-5 w-5" />
        </button> */}
      </div>
    </div>
  );

  const renderTheme = () => {
    switch (card.theme) {
      case 'modern':
        return renderModernTheme();
      case 'classic':
        return renderClassicTheme();
      case 'minimal':
        return renderMinimalTheme();
      default:
        return renderModernTheme();
    }
  };

  return (
    <div className={className}>
      {renderTheme()}
    </div>
  );
};

export default BusinessCard; 